/**
 * Dashboard Page - Modern CRM style
 */

import { Link } from 'react-router-dom';
import {
  Calendar,
  Wrench,
  CheckCircle2,
  Users,
  AlertCircle,
  RefreshCw,
  Car,
  ChevronRight,
  Clock,
  Mail,
  ThumbsUp,
  Timer,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  MetricCard,
  MetricCardSkeleton,
  QuickActions,
  AgendaDashboardPanel,
  JobBoardOverview,
} from '../components/features/dashboard';
import { useDashboard, useDashboardActivity } from '../hooks/useDashboard';
import type { RecentBooking, ActivityEvent } from '../services/dashboardApi';
import { cn } from '../lib/utils';
import { pageStyles, cardStyles, iconSizes } from '../lib/styles';
import { StatusBadge } from '../components/ui/StatusBadge';
import type { UnifiedCaseType } from '@leal-crm/shared';

function formatDateTime(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  const dateLabel = date.toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
  });
  const timeLabel = date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${dateLabel} ${timeLabel}`;
}

function RecentBookingItem({ booking }: { booking: RecentBooking }) {
  const bookingReference = booking.bookingReference || `#${booking.id.slice(0, 8)}`;
  const status = booking.status || booking.workflowStage || 'CONFIRMED';

  return (
    <Link
      to={`/cases/${booking.caseId}`}
      className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-gray-50 transition-colors group"
    >
      <div className="flex h-7 w-7 items-center justify-center rounded-md bg-gray-100 group-hover:bg-gray-200 transition-colors flex-shrink-0">
        <Car className={cn(iconSizes.sm, 'text-gray-500')} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-gray-900 truncate">
            {booking.customer.name}
          </span>
          <span className="text-[10px] text-gray-400">
            {booking.vehicle.registration}
          </span>
          <span className="text-[10px] text-gray-400 font-mono">
            Ref {bookingReference}
          </span>
        </div>
        <div className="flex items-center gap-1 mt-0.5">
          <Clock className={cn(iconSizes.xs, 'text-gray-400')} />
          <span className="text-[10px] text-gray-500">
            {formatDateTime(booking.scheduledAt)} - {booking.vehicle.make} {booking.vehicle.model}
          </span>
        </div>
      </div>
      <StatusBadge status={status} bookingType={booking.type as UnifiedCaseType} size="xs" />
      <ChevronRight className={cn(iconSizes.sm, 'text-gray-300 group-hover:text-gray-400 transition-colors')} />
    </Link>
  );
}

function ActivityItem({ event }: { event: ActivityEvent }) {
  const timestamp = new Date(event.createdAt * 1000);
  const timeLabel = timestamp.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  const bookingRef = event.booking.reference || `#${event.booking.id.slice(0, 8)}`;

  if (event.type === 'booking_created') {
    return (
      <Link
        to={`/cases/${event.booking.caseId}`}
        className="flex items-start gap-2 px-2 py-2 rounded-md hover:bg-gray-50 group"
      >
        <div className="flex h-7 w-7 items-center justify-center rounded-md bg-blue-50 text-blue-600 flex-shrink-0">
          <Calendar className={cn(iconSizes.xs)} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-900">
            Nueva reserva <span className="font-medium">{event.booking.customerName || 'Cliente'}</span>
          </p>
          <p className="text-[10px] text-gray-500 truncate">
            {event.booking.vehicleRegistration || '—'} · {bookingRef}
          </p>
        </div>
        <span className="text-[10px] text-gray-400">{timeLabel}</span>
      </Link>
    );
  }

  const statusChange = event.statusChange;
  const toInfo = statusChange?.toInfo;
  const fromInfo = statusChange?.fromInfo;
  const toLabel = toInfo?.name || statusChange?.to;
  const fromLabel = fromInfo?.name || statusChange?.from;
  const iconBg = toInfo?.bgColor || '#FEF3C7';
  const iconColor = toInfo?.color || '#D97706';

  return (
    <Link
      to={`/cases/${event.booking.caseId}`}
      className="flex items-start gap-2 px-2 py-2 rounded-md hover:bg-gray-50 group"
    >
      <div
        className="flex h-7 w-7 items-center justify-center rounded-md flex-shrink-0"
        style={{ backgroundColor: iconBg }}
      >
        <CheckCircle2 className={cn(iconSizes.xs)} style={{ color: iconColor }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-900">
          {fromLabel && toLabel ? (
            <>
              <span
                className="inline-flex px-1 py-0.5 rounded text-[10px] font-medium"
                style={fromInfo ? { color: fromInfo.color, backgroundColor: fromInfo.bgColor || undefined } : undefined}
              >
                {fromLabel}
              </span>
              <span className="mx-1 text-gray-400">→</span>
              <span
                className="inline-flex px-1 py-0.5 rounded text-[10px] font-medium"
                style={toInfo ? { color: toInfo.color, backgroundColor: toInfo.bgColor || undefined } : undefined}
              >
                {toLabel}
              </span>
            </>
          ) : (
            event.eventType || 'Actualizacion'
          )}
        </p>
        <p className="text-[10px] text-gray-500 truncate">
          {event.booking.customerName || 'Cliente'} · {bookingRef}
        </p>
        {event.note && <p className="text-[10px] text-gray-400 truncate">{event.note}</p>}
      </div>
      <span className="text-[10px] text-gray-400">{timeLabel}</span>
    </Link>
  );
}

function BookingItemSkeleton() {
  return (
    <div className="flex items-center gap-3 px-3 py-2.5">
      <div className="h-8 w-8 rounded-lg bg-gray-100 animate-pulse" />
      <div className="flex-1 space-y-1.5">
        <div className="h-3.5 w-28 bg-gray-100 rounded animate-pulse" />
        <div className="h-3 w-36 bg-gray-100 rounded animate-pulse" />
      </div>
      <div className="h-5 w-16 bg-gray-100 rounded-full animate-pulse" />
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-red-200 bg-red-50 p-6 text-center">
      <AlertCircle className="h-8 w-8 text-red-500 mb-3" />
      <h3 className="text-sm font-medium text-red-800 mb-1">Error al cargar datos</h3>
      <p className="text-xs text-red-600 mb-3 max-w-xs">{message}</p>
      <button
        onClick={onRetry}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
      >
        <RefreshCw className="h-3 w-3" />
        Reintentar
      </button>
    </div>
  );
}

export function DashboardPage() {
  const { user } = useAuth();
  const { data, isLoading, isError, error, refetch } = useDashboard();
  const { data: activityData, isLoading: isLoadingActivity } = useDashboardActivity(20);

  if (isError) {
    return (
      <div className={pageStyles.container}>
        <div>
          <h1 className={pageStyles.title}>Dashboard</h1>
          <p className={pageStyles.subtitle}>
            Bienvenido, {user?.name}
          </p>
        </div>
        <ErrorState
          message={error?.message || 'No se pudieron cargar las metricas'}
          onRetry={() => refetch()}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left Section - Main Content */}
      <div className="lg:col-span-2 space-y-4">
        {/* Header */}
        <div className={pageStyles.header}>
          <div>
            <h1 className={pageStyles.title}>Dashboard</h1>
            <p className={pageStyles.subtitle}>
              Bienvenido, {user?.name}. Resumen del dia.
            </p>
          </div>
          <QuickActions />
        </div>

        {/* Metrics */}
        <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
          {isLoading ? (
            <>
              <MetricCardSkeleton />
              <MetricCardSkeleton />
              <MetricCardSkeleton />
              <MetricCardSkeleton />
            </>
          ) : (
            <>
              <MetricCard
                title="Citas hoy"
                value={data?.todaysBookings ?? 0}
                icon={Calendar}
                href="/bookings"
                variant="default"
              />
              <MetricCard
                title="En reparacion"
                value={data?.activeRepairs ?? 0}
                icon={Wrench}
                href="/jobs"
                variant="warning"
              />
              <MetricCard
                title="Pend. entrega"
                value={data?.pendingDelivery ?? 0}
                icon={CheckCircle2}
                href="/jobs?status=entrega"
                variant="success"
              />
              <MetricCard
                title="Clientes"
                value={data?.totalCustomers ?? 0}
                icon={Users}
                href="/customers"
              />
            </>
          )}
        </div>

        {/* KPI Cards */}
        {!isLoading && (
          <div className="grid gap-3 grid-cols-1 md:grid-cols-3">
            <MetricCard
              title="Entrega email"
              value={
                data?.emailDeliveryRate === null || data?.emailDeliveryRate === undefined
                  ? '—'
                  : `${Math.round(data.emailDeliveryRate * 100)}%`
              }
              icon={Mail}
              variant="default"
              trendLabel="Ultimos 30 dias"
            />
            <MetricCard
              title="Aprobacion"
              value={
                data?.approvalRate === null || data?.approvalRate === undefined
                  ? '—'
                  : `${Math.round(data.approvalRate * 100)}%`
              }
              icon={ThumbsUp}
              variant="success"
              trendLabel="Presupuestos"
            />
            <MetricCard
              title="Ciclo promedio"
              value={
                data?.avgCycleTimeHours === null || data?.avgCycleTimeHours === undefined
                  ? '—'
                  : `${(data.avgCycleTimeHours / 24).toFixed(1)}d`
              }
              icon={Timer}
              variant="warning"
              trendLabel="Creado → cerrado"
            />
          </div>
        )}

        {/* Job Board Overview */}
        <JobBoardOverview />

        {/* Content Cards */}
        <div className="grid gap-3 md:grid-cols-2">
          {/* Recent Bookings */}
          <div className={cardStyles.container}>
            <div className={cardStyles.header}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Calendar className={cn(iconSizes.sm, 'text-gray-400')} />
                  <h3 className="text-xs font-medium text-gray-900">Reservas recientes</h3>
                </div>
                <Link to="/bookings" className="text-[10px] text-blue-600 hover:text-blue-700 font-medium">
                  Ver todas
                </Link>
              </div>
            </div>
            <div className={cardStyles.contentCompact}>
              {isLoading ? (
                <div className="space-y-1">
                  <BookingItemSkeleton />
                  <BookingItemSkeleton />
                  <BookingItemSkeleton />
                </div>
              ) : !data?.recentBookings || data.recentBookings.length === 0 ? (
                <div className="py-6 text-center">
                  <Calendar className={cn(iconSizes.lg, 'text-gray-300 mx-auto mb-2')} />
                  <p className="text-[10px] text-gray-500">Sin reservas recientes</p>
                  <Link
                    to="/bookings/new"
                    className="inline-flex items-center gap-1 mt-2 text-[10px] text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Nueva reserva
                    <ChevronRight className={iconSizes.xs} />
                  </Link>
                </div>
              ) : (
                <div className="space-y-0.5">
                  {data.recentBookings.slice(0, 4).map((booking) => (
                    <RecentBookingItem key={booking.id} booking={booking} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Active Repairs - All workshop activity */}
          <div className={cardStyles.container}>
            <div className={cardStyles.header}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Wrench className={cn(iconSizes.sm, 'text-gray-400')} />
                  <h3 className="text-xs font-medium text-gray-900">Actividad en taller</h3>
                </div>
                <Link to="/jobs" className="text-[10px] text-blue-600 hover:text-blue-700 font-medium">
                  Ver tablero
                </Link>
              </div>
            </div>
            <div className={cardStyles.contentCompact}>
              {isLoading ? (
                <div className="space-y-1">
                  <BookingItemSkeleton />
                  <BookingItemSkeleton />
                </div>
              ) : (() => {
                const workshopStatuses = ['DROPOFF_COMPLETE'];
                const workshopBookings = data?.recentBookings?.filter((b) => {
                  const status = b.status || b.workflowStage;
                  return status ? workshopStatuses.includes(status) : false;
                }) || [];

                if (workshopBookings.length === 0) {
                  return (
                    <div className="py-6 text-center">
                      <Wrench className={cn(iconSizes.lg, 'text-gray-300 mx-auto mb-2')} />
                      <p className="text-[10px] text-gray-500">Sin vehiculos en taller</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-0.5">
                    {workshopBookings.slice(0, 5).map((booking) => (
                      <RecentBookingItem key={booking.id} booking={booking} />
                    ))}
                    {workshopBookings.length > 5 && (
                      <p className="text-center text-[10px] text-gray-400 py-2">
                        +{workshopBookings.length - 5} mas en taller
                      </p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Right Section - Agenda Panel */}
      <div className="lg:col-span-1">
        <div className="sticky top-4 space-y-4">
          <AgendaDashboardPanel />

          <div className={cardStyles.container}>
            <div className={cardStyles.header}>
              <div className="flex items-center gap-2">
                <Clock className={cn(iconSizes.sm, 'text-gray-400')} />
                <h3 className="text-xs font-medium text-gray-900">Actividad reciente</h3>
              </div>
            </div>
            <div className={cardStyles.contentCompact}>
              {isLoadingActivity ? (
                <div className="space-y-1">
                  <BookingItemSkeleton />
                  <BookingItemSkeleton />
                </div>
              ) : !activityData?.items || activityData.items.length === 0 ? (
                <div className="py-6 text-center">
                  <Clock className={cn(iconSizes.lg, 'text-gray-300 mx-auto mb-2')} />
                  <p className="text-[10px] text-gray-500">Sin actividad reciente</p>
                </div>
              ) : (
                <div className="space-y-0.5">
                  {activityData.items.map((event) => (
                    <ActivityItem key={`${event.type}-${event.id}`} event={event} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
